import {
  BUTTON_COLOR,
  BUTTON_ICON_GAP,
  BUTTON_SIZE,
  BUTTON_VARIANT,
  BUTTON_WIDTH,
} from '../../../theme/v2/button';
import {
  ButtonChildrenIconType,
  ButtonColor,
  ButtonIconGap,
  ButtonSize,
  ButtonTextIconType,
  ButtonVariant,
  ButtonWidth,
  IconButtonType,
} from '../../../types/v2/button';
import Icon from '../../icons/icon';
import ButtonBase from './button.base';

// TODO
/* Limitation: Icon color is not dependent on the color of the button
 * or its variant type.
 *
 * Possible solution: Create a map that maps the icon color for a button
 * mapped on the buttons variant and color as the params
 */

function getIconColor(color: ButtonColor) {
  if (
    color === 'accent' ||
    color === 'glass' ||
    color === 'secondary' ||
    color === 'base-100' ||
    color === 'base-200'
  ) {
    return 'primary';
  } else {
    return 'secondary';
  }
}

type ButtonProps = {
  onClick?: Function;
  variant?: ButtonVariant;
  loadingText?: string;
  disabled?: boolean;
  loading?: boolean;
  width?: ButtonWidth;
  size?: ButtonSize;
  type?: 'submit' | 'button';
  color?: ButtonColor;
  iconSpacing?: ButtonIconGap;
  withButtonStyle?: boolean;
} & (ButtonChildrenIconType | ButtonTextIconType | IconButtonType);

const TEXT_BUTTON_COLOR = {
  primary: 'text-primary',
  secondary: 'text-secondary',
  accent: 'text-accent',
  success: 'text-success',
  danger: 'text-error',
  glass: 'text-glass',
  'base-100': 'text-base-100',
  'base-200': 'text-base-100',
};

export default function Button(props: ButtonProps) {
  const {
    children,
    loading,
    loadingText,
    color,
    disabled,
    text,
    asIcon,
    rightIcon,
    leftIcon,
    onClick,
    variant,
    size,
    iconSpacing,
    width,
    type,
    withButtonStyle,
  } = props;

  const node = children || text;

  const handleOnClick = onClick || (() => {});

  // defaults
  const computedLoadingText = loadingText || 'loading';

  // styles
  const buttonIconGap = BUTTON_ICON_GAP[iconSpacing || 'base'];
  const buttonVariant = BUTTON_VARIANT[variant || 'filled'];
  const buttonColor = BUTTON_COLOR[color || 'primary'];
  const buttonWidth = BUTTON_WIDTH[width || 'fit'];
  const buttonSize = BUTTON_SIZE[size || 'base'];
  const buttonLoading = loading ? 'loading' : '';

  const DEFAULT_TEXT_BUTTON_STYLE = `inline-flex justify-center items-center uppercase hover:contrast-150 h-[2rem] ${buttonWidth} ${
    TEXT_BUTTON_COLOR[color || 'primary']
  }`;

  const DEFAULT_ICON_BUTTON_STYLE =
    'icon-btn w-fit rounded-full cursor-pointer p-1';

  // generate raw style class list
  const rawStyle =
    `btn no-animation ${buttonSize} ${buttonIconGap} ${buttonWidth} ${buttonVariant} ${buttonColor}  ${buttonLoading}`
      .replaceAll(/[^\S\r\n]{2,}/g, ' ')
      .trimEnd();

  // add extra classes required for specific kinds of buttons
  const buttonStyle = `px-5 rounded-full ${rawStyle}`;

  const iconButtonStyle = withButtonStyle
    ? `${buttonVariant} ${buttonColor} ${DEFAULT_ICON_BUTTON_STYLE}`
    : DEFAULT_ICON_BUTTON_STYLE;

  // button types
  const textButton = variant === 'text';

  return (
    <>
      {!asIcon && !loading && (
        <button
          disabled={disabled}
          type={type}
          className={textButton ? DEFAULT_TEXT_BUTTON_STYLE : buttonStyle}
          onClick={() => handleOnClick()}
        >
          {leftIcon && (
            <Icon
              icon={leftIcon}
              color={color === 'secondary' ? 'primary' : 'secondary'}
            />
          )}
          <ButtonBase textSize={size} text={node} />
          {rightIcon && (
            <Icon
              icon={rightIcon}
              color={color === 'secondary' ? 'primary' : 'secondary'}
            />
          )}
        </button>
      )}
      {!asIcon && loading && (
        <button
          disabled={disabled}
          type={type}
          className={textButton ? DEFAULT_TEXT_BUTTON_STYLE : buttonStyle}
          onClick={() => handleOnClick()}
        >
          {textButton && <ButtonBase textSize={size} text={''} />}
          {!textButton && (
            <ButtonBase textSize={size} text={computedLoadingText} />
          )}
        </button>
      )}
      {asIcon && (
        <div
          className={iconButtonStyle}
          onClick={(e) => {
            e.stopPropagation();
            handleOnClick();
          }}
        >
          {loading && <></>}
          {!loading && (
            <Icon
              size={size}
              color={getIconColor(color || 'primary')}
              icon={asIcon}
            />
          )}
        </div>
      )}
    </>
  );
}
